/**
 * PollMessage — Poll display with voting bars
 *
 * Adapted from bachata-nation-next PollMessage.tsx.
 * Neubrutalism styling with border-gray-200.
 */

import { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '@/features/auth';
import type { PollData } from '../types';

interface PollMessageProps {
  pollData: PollData;
  messageId: string;
  onVote?: (messageId: string, optionIndex: number) => void;
  className?: string;
}

export const PollMessage = memo(function PollMessage({
  pollData,
  messageId,
  onVote,
  className = '',
}: PollMessageProps) {
  const { t } = useTranslation();
  const currentUserId = useAuthStore((s) => s.user?.id);

  const { question, options, votes } = pollData;

  const totalVotes = useMemo(() => {
    return Object.values(votes).reduce((sum, voters) => sum + voters.length, 0);
  }, [votes]);

  const myVote = useMemo(() => {
    if (!currentUserId) return -1;
    for (const [idx, voters] of Object.entries(votes)) {
      if (voters.includes(currentUserId)) return Number(idx);
    }
    return -1;
  }, [votes, currentUserId]);

  const hasVoted = myVote >= 0;

  return (
    <div
      className={`w-full max-w-xs rounded-2xl border border-white/20 bg-white/80 p-3 glass-tinted ${className}`}
    >
      {/* Question */}
      <div className="mb-3 text-sm font-bold text-neutral-800" dir="auto">
        {question}
      </div>

      {/* Options */}
      <div className="flex flex-col gap-2">
        {options.map((option, idx) => {
          const optionVoters = votes[String(idx)] || [];
          const voteCount = optionVoters.length;
          const percentage = totalVotes > 0 ? Math.round((voteCount / totalVotes) * 100) : 0;
          const isMyVote = myVote === idx;

          return (
            <button
              key={idx}
              type="button"
              onClick={() => onVote?.(messageId, idx)}
              disabled={hasVoted}
              className={`relative overflow-hidden rounded-lg border px-3 py-2 text-start text-sm transition-all ${
                isMyVote
                  ? 'border-slate-400 bg-white/30 font-bold text-neutral-800'
                  : hasVoted
                    ? 'border-white/20 bg-white/30 text-neutral-700'
                    : 'border-white/20 bg-white/60 text-neutral-800 hover:bg-white/10 active:bg-white/10'
              }`}
            >
              {/* Progress bar background */}
              {hasVoted && (
                <div
                  className={`absolute inset-y-0 start-0 transition-all duration-500 ${
                    isMyVote ? 'bg-white/30' : 'bg-neutral-100'
                  }`}
                  style={{ width: `${percentage}%` }}
                />
              )}

              {/* Option content */}
              <div className="relative z-10 flex items-center justify-between gap-2">
                <span dir="auto" className="flex-1">
                  {isMyVote && (
                    <svg
                      className="me-1 inline-block h-3.5 w-3.5 text-slate-600"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="4 12 9 17 20 6" />
                    </svg>
                  )}
                  {option}
                </span>
                {hasVoted && (
                  <span className="text-xs font-bold tabular-nums text-neutral-500">
                    {percentage}%
                  </span>
                )}
              </div>
            </button>
          );
        })}
      </div>

      {/* Vote count */}
      <div className="mt-2 text-center text-[11px] text-neutral-400">
        {t('messaging.pollVotes', { count: totalVotes })}
      </div>
    </div>
  );
});

export default PollMessage;
